import React from 'react';
import { AbsoluteFill, Sequence, Audio } from 'remotion';
import { TransitionSeries, linearTiming } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { none } from '@remotion/transitions/none';
import { CompositorComposition } from './CompositorComposition';
import { CompositorManifest } from '../types/manifest';
import { TransitionType } from '../../../../types/scene';

interface TransitionCompositionProps {
  fromManifest: CompositorManifest | null;
  toManifest: CompositorManifest | null;
  transitionType: TransitionType;
  transitionDurationFrames: number;
  fromClipFrames: number;
  toClipFrames: number;
  canvasWidth: number;
  canvasHeight: number;
}

function getRemotionPresentation(type: TransitionType) {
  switch (type) {
    case 'cut':
      return none();
    case 'fade':
      return fade({ shouldFadeOutExitingScene: true });
    case 'crossfade':
      return fade({ shouldFadeOutExitingScene: true });
    case 'slide':
      return slide({ direction: 'from-right' });
    default:
      return none();
  }
}

const PlaceholderScene: React.FC<{ 
  text: string;
  gradient: string;
  canvasWidth: number;
  canvasHeight: number;
}> = ({ text, gradient, canvasWidth, canvasHeight }) => (
  <AbsoluteFill
    style={{
      width: canvasWidth,
      height: canvasHeight,
      background: gradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        color: 'white',
        fontSize: 32,
        fontWeight: 600,
        textShadow: '0 2px 8px rgba(0,0,0,0.3)',
        textAlign: 'center',
        padding: 40,
      }}
    >
      {text}
    </div>
  </AbsoluteFill>
);

const FromSceneWrapper: React.FC<{ manifest: CompositorManifest }> = ({ manifest }) => {
  return <CompositorComposition manifest={manifest} skipAudio />;
};

const ToSceneWrapper: React.FC<{ manifest: CompositorManifest }> = ({ manifest }) => {
  return <CompositorComposition manifest={manifest} skipAudio />;
};

const StandaloneAudioLayer: React.FC<{
  manifest: CompositorManifest | null;
  startAtFrame: number;
  durationFrames: number;
}> = ({ manifest, startAtFrame, durationFrames }) => {
  if (!manifest?.audioLayer?.segments) return null;

  return (
    <>
      {manifest.audioLayer.segments.map(segment => {
        if (segment.source.status !== 'ready' || !segment.source.value) return null;
        if (segment.isMuted || segment.volume === 0) return null;

        const segStartInScene = segment.startFrame || 0;
        const globalStart = startAtFrame + segStartInScene;
        const availableFrames = durationFrames - segStartInScene;
        const segDuration = Math.min(segment.durationFrames, availableFrames);

        if (segDuration <= 0) return null;

        return (
          <Sequence
            key={`audio-standalone-${segment.segmentId}`}
            from={globalStart}
            durationInFrames={segDuration}
          >
            <Audio
              src={segment.source.value}
              volume={segment.volume}
              startFrom={segment.audioStartFrame || 0}
              pauseWhenBuffering
            />
          </Sequence>
        );
      })}
    </>
  );
};

export const TransitionComposition: React.FC<TransitionCompositionProps> = ({
  fromManifest,
  toManifest,
  transitionType,
  transitionDurationFrames,
  fromClipFrames,
  toClipFrames,
  canvasWidth,
  canvasHeight,
}) => {
  const presentation = getRemotionPresentation(transitionType);
  const effectiveTransitionFrames = transitionType === 'cut' ? 1 : transitionDurationFrames;
  const timing = linearTiming({ durationInFrames: effectiveTransitionFrames });

  const toSceneStartFrame = fromClipFrames - effectiveTransitionFrames;

  if (fromClipFrames <= 0 && toClipFrames <= 0) {
    console.error('[TransitionComposition] Both scenes have 0 frames!');
    return (
      <AbsoluteFill style={{ backgroundColor: '#f00', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'white', fontSize: 24 }}>Error: No frames to render</div>
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill
      style={{
        width: canvasWidth,
        height: canvasHeight,
        overflow: 'hidden',
        backgroundColor: '#000',
      }}
    >
      <TransitionSeries>
        <TransitionSeries.Sequence durationInFrames={Math.max(1, fromClipFrames)}>
          {fromManifest ? (
            <FromSceneWrapper manifest={fromManifest} />
          ) : (
            <PlaceholderScene
              text="Scene 1 (No Data)"
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
            />
          )}
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={presentation}
          timing={timing}
        />

        <TransitionSeries.Sequence durationInFrames={Math.max(1, toClipFrames)}>
          {toManifest ? (
            <ToSceneWrapper manifest={toManifest} />
          ) : (
            <PlaceholderScene
              text="Scene 2 (No Data)"
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
            />
          )}
        </TransitionSeries.Sequence>
      </TransitionSeries>

      <StandaloneAudioLayer
        manifest={fromManifest}
        startAtFrame={0}
        durationFrames={fromClipFrames}
      />

      <StandaloneAudioLayer
        manifest={toManifest}
        startAtFrame={Math.max(0, toSceneStartFrame)}
        durationFrames={toClipFrames}
      />
    </AbsoluteFill>
  );
};

export default TransitionComposition;
